import { useEffect, useState } from 'react';
import { useNotify, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { SectionCard, fmtDate } from '../../shared/Panel';
import { fromDateTimeLocalValue } from '../../shared/clientTime';
import { recordScoped } from '../../shared/recordScoped';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { SUBCONTRACTOR_RULE_LABELS } from '../publications/publicationsApi';
import { useHasAnyPermission } from '../PermissionGate';
import { RULE_OPTIONS, ruleColor, setByLabel } from './SubcontractorRuleSection';
import {
  RULE_CHANGE_STATE_LABELS,
  cancelScheduledRule,
  fetchRuleHistory,
  fetchRuleOwners,
  ruleHistoryChangeText,
  saveRuleChange,
  type RuleHistoryEntry,
  type RuleOwnerOption,
  type ScheduledRule,
} from './subcontractorRuleApi';

/**
 * Reguła podwykonawcy klienta z właścicielem, datą obowiązywania, zmianą
 * zaplanowaną i historią (prace 25.10.2026, S-25.10-06, przełącznik
 * `subcontractorRuleOwnership`; decyzja użytkownika 24.10.2026 pkt 8,
 * Odpowiedzi CS 20.10.2026 pkt 6: „chcą sami zaznaczać/edytować”). Wzorzec:
 * parametry rekomendacji i stawki spółki (S-24.10-12). Zmieniają CS
 * i administracja; rozstrzyga API.
 */

export const OWNERSHIP_NOTE =
  'Reguła ma właściciela — osobę odpowiedzialną za jej treść — i datę, od której obowiązuje. ' +
  'Zmianę można zaplanować: do wskazanej daty publikacja i zapytania do przewoźników ' +
  'korzystają z obecnej reguły. Każda zmiana wymaga powodu i trafia do historii.';

const ownerText = (r: RaRecord) => {
  const o = r.subcontractorRuleOwner as { name?: string | null } | null | undefined;
  return o?.name ? o.name : 'właściciel nieustalony';
};

const SubcontractorRuleOwnershipPanel = () => {
  const r = useRecordContext<RaRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const canEdit = useHasAnyPermission(['ADMIN_SECTION', 'INTAKE_CS']);
  const current = (r?.subcontractorRule as string | null) ?? null;
  const currentOwner = (r?.subcontractorRuleOwner as { id?: number } | null)?.id ?? null;
  const [rule, setRule] = useState<string>(current ?? '');
  const [notes, setNotes] = useState<string>((r?.subcontractorNotes as string | null) ?? '');
  const [ownerId, setOwnerId] = useState<number | ''>(currentOwner ?? '');
  const [validFrom, setValidFrom] = useState('');
  const [reason, setReason] = useState('');
  const [owners, setOwners] = useState<RuleOwnerOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState<string | null>(null);

  useEffect(() => {
    if (!canEdit) return;
    let active = true;
    fetchRuleOwners()
      .then((list) => {
        if (active) setOwners(list);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [canEdit]);

  if (!r) return null;
  const scheduled = (r.subcontractorRuleScheduled as ScheduledRule | null | undefined) ?? null;
  const validFromNow = r.subcontractorRuleValidFrom as string | null | undefined;

  const save = async () => {
    if (reason.trim() === '') {
      notify('Podaj powód zmiany reguły', { type: 'error' });
      return;
    }
    setBusy(true);
    try {
      const nextRule = rule === '' ? null : rule;
      const scheduling = validFrom.trim() !== '';
      const nextOwner = ownerId === '' ? null : ownerId;
      await saveRuleChange(r.id as number, {
        rule: scheduling || nextRule !== current ? nextRule : undefined,
        notes,
        reason: reason.trim(),
        validFrom: scheduling ? fromDateTimeLocalValue(validFrom) : null,
        ownerId: nextOwner === currentOwner ? undefined : nextOwner,
      });
      notify(scheduling ? 'Zmiana reguły zaplanowana' : 'Reguła podwykonawcy zapisana', { type: 'success' });
      setReason('');
      setValidFrom('');
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!scheduled?.changeId) return;
    if (!cancelReason || cancelReason.trim() === '') {
      notify('Podaj powód odwołania', { type: 'error' });
      return;
    }
    setBusy(true);
    try {
      await cancelScheduledRule(r.id as number, scheduled.changeId, cancelReason.trim());
      notify('Zmiana zaplanowana odwołana', { type: 'success' });
      setCancelReason(null);
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title="Podwykonawca — reguła publikacji na giełdach">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="rule-ownership-note">
        {OWNERSHIP_NOTE}
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
        <Chip
          size="small"
          color={ruleColor(current)}
          label={SUBCONTRACTOR_RULE_LABELS[current ?? 'nieustalona'] ?? current}
        />
        <Typography variant="caption" color="text.secondary" data-testid="rule-valid-from">
          {validFromNow ? `obowiązuje od ${fmtDate(validFromNow)}` : 'obowiązuje od wcześniejszego zapisu'}
          {r.subcontractorSetAt ? `; ustawiono ${fmtDate(r.subcontractorSetAt as string)}${setByLabel(r)}` : ''}
        </Typography>
        <Typography variant="caption" data-testid="rule-owner">
          Właściciel: {ownerText(r)}
        </Typography>
      </Stack>
      {scheduled ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }} data-testid="rule-scheduled">
          <Chip
            size="small"
            variant="outlined"
            color="info"
            label={`zaplanowana od ${fmtDate(scheduled.validFrom)}: ${scheduled.label}`}
          />
          {canEdit && scheduled.changeId ? (
            <Button size="small" color="warning" onClick={() => setCancelReason('')} disabled={busy}>
              Odwołaj
            </Button>
          ) : null}
        </Stack>
      ) : null}
      {r.subcontractorNotes ? (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {String(r.subcontractorNotes)}
        </Typography>
      ) : null}

      {canEdit ? (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }} useFlexGap>
            <FormControl size="small" fullWidth={false} sx={{ minWidth: 320 }}>
              <InputLabel id="regula-podwykonawcy-wl">Reguła</InputLabel>
              <Select
                labelId="regula-podwykonawcy-wl"
                label="Reguła"
                value={rule}
                onChange={(e) => setRule(String(e.target.value))}
              >
                {RULE_OPTIONS.map((o) => (
                  <MenuItem key={o.kod || 'brak'} value={o.kod}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth={false} sx={{ minWidth: 240 }} data-testid="rule-owner-select">
              <InputLabel id="wlasciciel-reguly">Właściciel</InputLabel>
              <Select
                labelId="wlasciciel-reguly"
                label="Właściciel"
                value={ownerId === '' ? '' : String(ownerId)}
                onChange={(e) => setOwnerId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value="">nieustalony</MenuItem>
                {owners.map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>
                    {o.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="datetime-local"
              size="small"
              fullWidth={false}
              label="Obowiązuje od (czas polski)"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              helperText="Puste — od razu. Data w przyszłości — zmiana zaplanowana."
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'rule-valid-from-input' } }}
              sx={{ minWidth: 240 }}
            />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }} useFlexGap>
            <TextField
              size="small"
              fullWidth={false}
              label="Uwagi (skąd reguła, np. mail klienta z datą)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ minWidth: 360, flex: '1 1 360px' }}
            />
            <TextField
              size="small"
              fullWidth={false}
              required
              label="Powód zmiany"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'rule-reason-input' } }}
              sx={{ minWidth: 280, flex: '1 1 280px' }}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={busy}
              onClick={() => void save()}
              data-testid="rule-save"
            >
              Zapisz regułę
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Regułę ustawiają CS i administrator.
        </Typography>
      )}

      <Dialog open={cancelReason !== null} onClose={() => setCancelReason(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Odwołać zmianę zaplanowaną?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {scheduled ? `${scheduled.label} od ${fmtDate(scheduled.validFrom)}. ` : ''}
            Zostaje obecna reguła.
          </Typography>
          <TextField
            label="Powód odwołania"
            value={cancelReason ?? ''}
            onChange={(e) => setCancelReason(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            required
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelReason(null)} disabled={busy}>
            Wróć
          </Button>
          <Button variant="contained" color="warning" onClick={() => void cancel()} disabled={busy}>
            Odwołaj zmianę
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

export const SubcontractorRuleOwnershipSection = recordScoped(SubcontractorRuleOwnershipPanel);

/** Historia reguły i właściciela — było, jest, od kiedy, kto, powód, stan. */
const SubcontractorRuleOwnershipHistoryPanel = () => {
  const r = useRecordContext<RaRecord>();
  const [rows, setRows] = useState<RuleHistoryEntry[] | null>(null);
  const id = r?.id;
  const key = [
    r?.subcontractorRule,
    r?.subcontractorRuleValidFrom,
    JSON.stringify(r?.subcontractorRuleScheduled ?? null),
    JSON.stringify(r?.subcontractorRuleOwner ?? null),
    r?.subcontractorNotes,
  ].join('|');
  useEffect(() => {
    if (id === undefined) return;
    let active = true;
    fetchRuleHistory(id as number)
      .then((data) => {
        if (active) setRows(data);
      })
      .catch(() => {
        if (active) setRows([]);
      });
    return () => {
      active = false;
    };
  }, [id, key]);
  if (!r) return null;
  return (
    <SectionCard title="Historia reguły podwykonawcy">
      {rows === null ? null : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Reguły jeszcze nie zmieniano.
        </Typography>
      ) : (
        <Box data-testid="rule-ownership-history" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kiedy</TableCell>
                <TableCell>Kto</TableCell>
                <TableCell>Zmiana</TableCell>
                <TableCell>Od kiedy</TableCell>
                <TableCell>Stan</TableCell>
                <TableCell>Powód</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((h) => (
                <TableRow key={h.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(h.changedAt)}</TableCell>
                  <TableCell>{h.changedByName ?? '—'}</TableCell>
                  <TableCell>
                    {ruleHistoryChangeText(h)}
                    {h.source === 'file' ? (
                      <Typography variant="caption" color="text.secondary" component="div">
                        plik „{h.fileName ?? '—'}”{h.fileLine ? `, wiersz ${h.fileLine}` : ''}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {h.kind === 'owner' ? '—' : fmtDate(h.validFrom ?? h.changedAt)}
                  </TableCell>
                  <TableCell>
                    {RULE_CHANGE_STATE_LABELS[h.state] ?? h.state}
                    {h.state === 'cancelled' ? (
                      <Typography variant="caption" color="text.secondary" component="div">
                        {h.cancelledByName ?? '—'}: {h.cancelReason ?? ''}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{h.reason ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </SectionCard>
  );
};

export const SubcontractorRuleOwnershipHistory = recordScoped(SubcontractorRuleOwnershipHistoryPanel);
